import { useState } from 'react';
import { useAuth } from '@/api';
import { SocialAuthButton } from './SocialAuthButton';
import type { SocialProvider } from '@/contexts/AuthContext';

type SocialLoginButtonsProps = {
  onError?: (error: string) => void;
};

/**
 * Social login buttons for Facebook, Apple, and Google.
 *
 * Brand chrome lives in `SocialAuthButton`; this component owns provider
 * selection, ordering, and the OAuth redirect. On web, clicking a button
 * redirects the browser through the Cognito Hosted UI → social provider → back
 * to the app with an auth code.
 */
export function SocialLoginButtons({ onError }: SocialLoginButtonsProps) {
  const { socialLogin } = useAuth();
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  const handleSocialLogin = async (provider: SocialProvider) => {
    const providerName =
      typeof provider === 'string'
        ? provider
        : (provider as { custom: string }).custom || 'custom provider';
    setLoadingProvider(providerName);
    try {
      await socialLogin(provider);
      // Browser will redirect away — loading state persists intentionally
    } catch (err) {
      setLoadingProvider(null);
      const message = err instanceof Error ? err.message : `Failed to sign in with ${providerName}`;
      onError?.(message);
    }
  };

  const isLoading = loadingProvider !== null;
  return (
    <div className="space-y-3">
      <SocialAuthButton
        provider="Facebook"
        iconPosition="right"
        label={loadingProvider === 'Facebook' ? 'Redirecting...' : 'Continue with Facebook'}
        hideIcon={loadingProvider === 'Facebook'}
        onClick={() => void handleSocialLogin('Facebook')}
        disabled={isLoading}
        data-testid="facebook-social-button"
      />

      <SocialAuthButton
        provider="Apple"
        iconPosition="right"
        label={loadingProvider === 'Apple' ? 'Redirecting...' : 'Continue with Apple'}
        hideIcon={loadingProvider === 'Apple'}
        onClick={() => void handleSocialLogin('Apple')}
        disabled={isLoading}
        data-testid="apple-social-button"
      />

      <SocialAuthButton
        provider="Google"
        iconPosition="right"
        label={loadingProvider === 'Google' ? 'Redirecting...' : 'Continue with Google'}
        hideIcon={loadingProvider === 'Google'}
        onClick={() => void handleSocialLogin('Google')}
        disabled={isLoading}
        data-testid="google-social-button"
      />
    </div>
  );
}

export default SocialLoginButtons;
